import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { MapStoreMunicipality } from '@app/shared/models/municipality/Municipality';
import { AuthService } from '@app/shared/services/auth.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, mergeMap, switchMap, takeUntil, tap } from 'rxjs/operators';
import { OverlayLoaderService } from '../../shared/services/overlay-loader.service';
import { SensorType } from '../models/sensor-type.model';
import { SensorTypesService } from './sensor-types.service';
import { excludedSensors, meterBasedSensors } from '@app/shared/constants';
import { ActiveSensor } from '../models/active-sensor';
import { PermissionCode } from '@app/shared/enums';

@Injectable({
  providedIn: 'root',
})
export class SensorTypeDataStoreService implements OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  private _sensorType: BehaviorSubject<SensorType[]> = new BehaviorSubject<SensorType[]>([]);
  private _selectedSensorType = new BehaviorSubject<SensorType | null>(null);

  public readonly sensorTypes$ = this._sensorType.asObservable();
  public readonly selectedSensorType$ = this._selectedSensorType.asObservable();

  public readonly sensorTypesTable$ = this._sensorType.pipe(
    map((types: SensorType[]) =>
      types.filter(
        (type: SensorType) => !meterBasedSensors.includes(type.code.toUpperCase()) && !excludedSensors.includes(type.code.toUpperCase()) && type.enabled
      )
    ),
    map((types: SensorType[]) => types.sort(this.compareSensorTypeNames))
  );

  public get sensorTypes() {
    return this._sensorType.getValue();
  }
  public get selectedSensorType() {
    return this._selectedSensorType.getValue();
  }

  public set selectedSensorType(value) {
    this._selectedSensorType.next(value);
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private overlayLoaderService: OverlayLoaderService,
    private sensortypesServices: SensorTypesService,
    private userMunicipalitiesService: UserMunicipalitiesService
  ) {
    this.setSelectedSensorType();

    this.userMunicipalitiesService.currentMunicipality$
      .pipe(
        filter((municipality: MapStoreMunicipality) => !!municipality?.guid && !!municipality?.key),
        distinctUntilChanged(),
        switchMap(() => this.authService.permissionLoadComplete$),
        filter((isLoaded: boolean) => isLoaded),
        filter(() => !!this.authService.permissions[PermissionCode.SENSOR_DATA]),
        switchMap(() => this.getSensorTypesBy(this.userMunicipalitiesService.currentMunicipality.key)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe({ next: (types: SensorType[]) => this._sensorType.next(types) });
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public findSensorType = (sensorTypeCode: string): Observable<SensorType> =>
    this.sensorTypes$.pipe(
      map((types: SensorType[]) => types.find((type: SensorType) => type.code.toLowerCase() === sensorTypeCode.toLowerCase()) as SensorType)
    );

  public findSensorTypeByName = (name: string): Observable<SensorType> =>
    this.sensorTypes$.pipe(map((types: SensorType[]) => types.find((type: SensorType) => type.name.toLowerCase() === name.toLowerCase()) as SensorType));

  public refreshSensorTypes(): void {
    this.overlayLoaderService.isLoading = true;
    this.sensortypesServices
      .refreshDeviceTypes()
      .pipe(
        switchMap(() => this.getSensorTypesBy(this.userMunicipalitiesService.currentMunicipality.key)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((types: SensorType[]) => {
        this._sensorType.next(types);
        this.overlayLoaderService.setLoadingToFalse();
      });
  }

  public refreshSensorTypesObservable(municipalityKey: string): Observable<SensorType[]> {
    return this.getSensorTypesBy(municipalityKey).pipe(tap((types: SensorType[]) => this._sensorType.next(types)));
  }

  private setSelectedSensorType = (): Subscription =>
    this.activatedRoute.queryParams
      .pipe(
        filter((param: Params) => !!param.t),
        map((param: Params): string => param.t),
        mergeMap(this.findSensorType),
        tap((type: SensorType) => this._selectedSensorType.next(type)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();

  private getSensorTypes = (): Observable<SensorType[]> => this.sensortypesServices.getSensorTypes();

  private getSensorTypesBy = (municipalityKey: string): Observable<SensorType[]> =>
    this.sensortypesServices.getSensorTypesBy(municipalityKey).pipe(
      mergeMap((municipalitySensors) => {
        const municipalitySensorCodes = municipalitySensors.map((sensor: ActiveSensor) => sensor.code);
        return this.getSensorTypes().pipe(
          map((allSensors: SensorType[]): SensorType[] => {
            return allSensors.map((sensor: SensorType): SensorType => {
              return {
                ...sensor,
                enabled: municipalitySensorCodes.includes(sensor.code),
              };
            });
          })
        );
      }),
      map((types: SensorType[]) => types.sort(this.compareSensorTypeNames))
    );

  private compareSensorTypeNames(aSensorType: SensorType, bSensorType: SensorType): number {
    if (aSensorType.name.toString().toLowerCase() < bSensorType.name.toString().toLowerCase()) return -1;
    if (aSensorType.name.toString().toLowerCase() > bSensorType.name.toString().toLowerCase()) return 1;
    return 0;
  }
}
